import React, { useEffect, useState, useMemo, useRef } from 'react';
import { apiKey } from '../../utils/ktcUtils';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { useParams } from 'react-router-dom';

function EnTranscript() {
  const [loading, setLoading] = useState(true);

  const { stdId } = useParams();
  const [student, setStudent] = useState(null);
  const [marks, setMarks] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${API_URL}/${ENDPOINTS.SYS}/getlink-apikey/transcript-generator-en/${stdId}`;

    setLoading(true);
    setError(null);

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        // report can be [] (no rows), [row], or nested arrays when the SQL
        // has several statements (SET / INSERT / SELECT). Find the row that
        // actually has the "student" field.
        const rows = Array.isArray(data?.report) ? data.report.flat(2) : [];
        const row = rows.find((r) => r && r.student);

        if (!row) {
          setError(
            data?.message ||
              data?.error ||
              `No transcript data found for student ${stdId}.`
          );
          setLoading(false);
          return;
        }

        const parse = (v) => (typeof v === 'string' ? JSON.parse(v) : v);

        setStudent(parse(row.student));
        setMarks(parse(row.marks) || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Transcript load error:', err);
        setError('Failed to load transcript. Please try again.');
        setLoading(false);
      });
  }, [stdId]);

  if (loading) {
    return <div className="text-center my-5" style={{ fontSize: '10px' }}>Loading transcript...</div>;
  }

  if (error) {
    return <div className="text-center my-5 text-danger" style={{ fontSize: '12px' }}>{error}</div>;
  }

  return <OfficialTranscript student={student} marks={marks} />;
}

// Small helper so header rows read "Label value" with the value italicized
// and NOT bold. No fixed width is used, so there's no artificial gap
// between the label and the value — just a normal single space.
const InfoLine = ({ label, value }) => (
  <p className="mb-0">
    {label} <span>{value}</span>
  </p>
);

const OfficialTranscript = ({ student, marks }) => {
  const studentData = {
    std_id: student?.std_id || 'EMS212975',
    name: student?.name || 'Mohamud Nur Mohamed DL',
    faculty: student?.faculty || 'Economics & Management Sciences',
    department: student?.department || 'Public Administration',
    batch: student?.batch || 'PA21 DL',
    Program_Duration: student?.['Program Duration'] || student?.Program_Duration || '2020 – 2024',
    serial_no: student?.serial_no || 'TR-20260913-951512',
    issued_date: student?.issued_date || '13/September/2026',
    grand_total: student?.grand_total || '-',
    average: student?.average || '-',
    grade: student?.grade || '-',
    gpa: student?.gpa || '-',
    degree_granted: student?.degree_granted || 'Bachelor of Public Administration',
    president: student?.president || 'Dr. Ahmed Hassan Barre',
    registrar: student?.registrar || 'Mr. Mohamed Omar Ali'
  };

  const groupedData = useMemo(() => {
    if (!marks || !Array.isArray(marks)) return [];

    const yearsMap = {};

    marks.forEach((item) => {
      const year = item.academic_year || 'Unknown Year';
      const semester = item.semester || 'Unknown Semester';

      if (!yearsMap[year]) {
        yearsMap[year] = {};
      }
      if (!yearsMap[year][semester]) {
        yearsMap[year][semester] = [];
      }

      yearsMap[year][semester].push(item);
    });

    // Sort courses by the NUMBER inside the course code
    // e.g. MHS101, ENG102, COM103, PHY104  (101 < 102 < 103 < 104)
    const codeNumber = (code) => {
      const m = String(code || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
    };
    const sortByCodeNumber = (courses) =>
      [...courses].sort((a, b) => {
        const diff = codeNumber(a.code) - codeNumber(b.code);
        return diff !== 0 ? diff : String(a.code || '').localeCompare(String(b.code || ''));
      });

    return Object.keys(yearsMap).map((year) => ({
      yearLabel: year,
      semesters: Object.keys(yearsMap[year]).map((semKey) => ({
        title: semKey,
        courses: sortByCodeNumber(yearsMap[year][semKey]),
        gpa: yearsMap[year][semKey][0]?.semester_gpa || '-',
        letter: yearsMap[year][semKey][0]?.semester_letter || '-'
      }))
    }));
  }, [marks]);

  // 8 semesters or fewer => everything must fit on ONE printed page
  // (and the size is scaled so it FILLS the page width and height).
  // More than 8 semesters => normal size, flows naturally onto page 2.
  const totalSemesters = groupedData.reduce((sum, y) => sum + y.semesters.length, 0);
  const isCompact = totalSemesters <= 8;

  const pageRef = useRef(null);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const PAGE_W_MM = 200;   // A4 210mm - 2 x 5mm margin
    const PAGE_H = 1075;     // A4 297mm - 2 x 5mm margin, in px (~1084) with a small safety gap
    const MAX_SCALE = 1.8;   // ugu badnaan intee la weyneyn karaa
    const MIN_SCALE = 0.5;   // ugu yaraan

    const applyLayout = (scale) => {
      // zoom = cabbirka (font, safaf, wax kasta), width = 200mm / scale
      // si haddii la weyneeyo, ballaca warqadda oo dhan uu buuxiyo
      el.style.setProperty('zoom', String(scale), 'important');
      el.style.setProperty('width', `${PAGE_W_MM / scale}mm`, 'important');
    };

    const beforePrint = () => {
      el.style.setProperty('max-width', 'none', 'important');
      el.style.setProperty('margin', '0 auto', 'important');

      if (!isCompact) {
        // > 8 semester: cabbir caadi ah, page 2 si dabiici ah
        applyLayout(1);
        return;
      }

      // <= 8 semester: raadi cabbirka ugu weyn ee 1 page ku haboon
      let best = MIN_SCALE;
      for (let s = MAX_SCALE; s >= MIN_SCALE; s -= 0.02) {
        applyLayout(1);
        el.style.setProperty('width', `${PAGE_W_MM / s}mm`, 'important');
        const h = el.offsetHeight; // dhererka layout-ka (zoom = 1)
        if (h * s <= PAGE_H) {
          best = s;
          break;
        }
      }
      applyLayout(best);
    };

    const afterPrint = () => {
      el.style.removeProperty('width');
      el.style.removeProperty('zoom');
      el.style.removeProperty('margin');
      el.style.removeProperty('max-width');
    };

    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [isCompact, groupedData]);

  const calculateTotalCredits = (courses) =>
    (courses || []).reduce((sum, item) => sum + (Number(item.credit_hours) || 0), 0);

  const calculateTotalMarks = (courses) => {
    const total = (courses || []).reduce((sum, item) => sum + (Number(item.marks) || 0), 0);
    return Math.round(total * 10) / 10;
  };

  const handlePrint = () => {
    window.print();
  };

  // Renders ONE year as a <tbody> that lives inside the single big table.
  // Both semesters of the year sit in the same <tr> (5 columns per side), so
  // left/right borders and row-heights can never drift apart even when a
  // long course name wraps onto two lines on one side only.
  const renderYearRows = (yearBlock, yearIdx) => {
    const sem1 = yearBlock.semesters[0];
    const sem2 = yearBlock.semesters[1];
    const maxRows = Math.max(sem1?.courses?.length || 0, sem2?.courses?.length || 0);
    const rows = Array.from({ length: maxRows });

    return (
      <tbody key={yearIdx} className="year-block">
        <tr className="bg-light text-center fw-bold">
          <th colSpan={10} className="py-1 text-center align-middle">{yearBlock.yearLabel}</th>
        </tr>
        <tr className="bg-light text-center fw-bold">
          <th colSpan={5} className="py-1 text-center align-middle">{sem1?.title || 'Semester'}</th>
          <th colSpan={5} className="py-1 text-center align-middle">{sem2?.title || 'Semester'}</th>
        </tr>
        <tr className="text-center table-light col-head">
          <th className="py-1 px-1 text-center align-middle">Course Code</th>
          <th className="py-1 px-1 text-center align-middle">Course Name</th>
          <th className="py-1 px-1 text-center align-middle">Credit Hours</th>
          <th className="py-1 px-1 text-center align-middle">Marks</th>
          <th className="py-1 px-1 text-center align-middle">G. Letter</th>
          <th className="py-1 px-1 text-center align-middle">Course Code</th>
          <th className="py-1 px-1 text-center align-middle">Course Name</th>
          <th className="py-1 px-1 text-center align-middle">Credit Hours</th>
          <th className="py-1 px-1 text-center align-middle">Marks</th>
          <th className="py-1 px-1 text-center align-middle">G. Letter</th>
        </tr>

        {maxRows === 0 ? (
          <tr>
            <td colSpan={10} className="text-center text-muted py-1">No courses</td>
          </tr>
        ) : (
          rows.map((_, idx) => {
            const c1 = sem1?.courses?.[idx];
            const c2 = sem2?.courses?.[idx];
            return (
              <tr key={idx}>
                <td className="text-center py-1 px-1 text-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c1?.code || ''}</td>
                <td className="py-1 px-1" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>{c1?.course || ''}</td>
                <td className="text-center py-1 px-1">{c1?.credit_hours ?? ''}</td>
                <td className="text-center py-1 px-1">{c1 ? (c1.marks ?? '-') : ''}</td>
                <td className="text-center py-1 px-1">{c1 ? (c1.letter || c1.grade_letter || c1.g_letter || '-') : ''}</td>

                <td className="text-center py-1 px-1 text-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c2?.code || ''}</td>
                <td className="py-1 px-1" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>{c2?.course || ''}</td>
                <td className="text-center py-1 px-1">{c2?.credit_hours ?? ''}</td>
                <td className="text-center py-1 px-1">{c2 ? (c2.marks ?? '-') : ''}</td>
                <td className="text-center py-1 px-1">{c2 ? (c2.letter || c2.grade_letter || c2.g_letter || '-') : ''}</td>
              </tr>
            );
          })
        )}

        {/* Total: label, sum under Credit Hours, sum under Marks */}
        <tr>
          <td colSpan={2} className="fw-bold py-1 px-1 text-center align-middle">
            <span style={{ display: 'inline-block', transform: 'translateX(-10px)' }}>Total</span>
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem1 ? calculateTotalCredits(sem1.courses) : ''}</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem1 ? calculateTotalMarks(sem1.courses) : ''}</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem1 ? sem1.letter : ''}</td>

          <td colSpan={2} className="fw-bold py-1 px-1 text-center align-middle">
            <span style={{ display: 'inline-block', transform: 'translateX(-10px)' }}>Total</span>
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem2 ? calculateTotalCredits(sem2.courses) : ''}</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem2 ? calculateTotalMarks(sem2.courses) : ''}</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem2 ? sem2.letter : ''}</td>
        </tr>
        <tr>
          <td colSpan={4} className="fw-bold py-1 px-1 text-center align-middle">Semester GPA</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem1 ? sem1.gpa : ''}</td>
          <td colSpan={4} className="fw-bold py-1 px-1 text-center align-middle">Semester GPA</td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem2 ? sem2.gpa : ''}</td>
        </tr>
      </tbody>
    );
  };

  return (
    <div className="container my-2">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=PT+Sans+Narrow:wght@400;700&display=swap');

        .transcript-container,
        .transcript-container * {
          font-family: 'PT Sans Narrow', Arial, sans-serif !important;
        }
        .transcript-container table,
        .transcript-container th,
        .transcript-container td {
          border: 1px solid #ccc !important;
          border-collapse: collapse;
        }
        /* Column headers (Course Code, Course Name, Credit Hours, Marks, G. Letter)
           are always centered, horizontally and vertically. */
        .transcript-container th {
          text-align: center !important;
          vertical-align: middle !important;
        }
        /* Student info header (top section): black */
        .transcript-container .row,
        .transcript-container .row p {
          color: #000 !important;
        }
        /* Signature block (names + titles): black */
        .transcript-container .signature-block,
        .transcript-container .signature-block * {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
          }
          .d-print-none {
            display: none !important;
          }
          .container {
            max-width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .transcript-container {
            border: none !important;
            box-shadow: none !important;
            padding: 2px !important;
            margin: 0 auto !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .year-block {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          table {
            border-collapse: collapse !important;
          }
          @page {
            size: A4 portrait;
            margin: 5mm;
          }
        }
      `}</style>

      {/* Print button */}
      <div className="text-end mb-2 d-print-none" style={{ maxWidth: '960px', margin: '0 auto' }}>
        <button onClick={handlePrint} className="btn btn-primary btn-sm fw-bold">
          Print Transcript
        </button>
      </div>

      <div
        ref={pageRef}
        className="container bg-white p-3 border shadow-sm transcript-container mx-auto"
        style={{
          maxWidth: '960px',
          fontFamily: '"PT Sans Narrow", Arial, sans-serif',
          fontSize: '9pt'
        }}
      >
        {/* Small blank space at the top of the paper */}
        <div style={{ height: '46px' }} />

        {/* Header */}
        <div
          className="row mb-2"
          style={{
            fontSize: '15px',
            fontWeight: 750,
            color: '#00000052',
            fontFamily: '"PT Sans Narrow", Arial, sans-serif'
          }}
        >
          <div className="col-8">
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Faculty of {studentData.faculty}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Department of {studentData.department}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Student Name: {studentData.name}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Student ID: {studentData.std_id}
            </p>
          </div>
          <div className="col-4" style={{ paddingLeft: '90px' }}>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Batch: {studentData.batch}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Program Duration: {studentData.Program_Duration}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Issued Date: {studentData.issued_date}
            </p>
            <p
              className="mb-0"
              style={{ fontFamily: 'PT Sans Narrow', fontStyle: 'normal' }}
            >
              Serial No: {studentData.serial_no}
            </p>
          </div>
        </div>

        <div className="text-center my-2">
  <h4 className="fw-bold tracking-wider" style={{ fontSize: '18px' }}>OFFICIAL TRANSCRIPT RECORD</h4>
</div>

        {/* ONE single table: all academic years + summary */}
        <div className="mb-2">
          <table
            className="table table-bordered table-sm mb-0"
            style={{
              fontSize: '9pt',
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'collapse'
            }}
          >
            <colgroup>
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '25.5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '25.5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
            </colgroup>

            {groupedData.map((yearBlock, yearIdx) => renderYearRows(yearBlock, yearIdx))}

            {/* Summary (inside the same table) */}
            <tbody className="year-block">
              {/* ONE row, aligned with the table columns above:
                  Grand Total  = Course Code + Course Name       (cols 1-2)
                  Average      = Credit + Marks                  (cols 3-4)
                  Grade        = G. Letter                       (col 5)
                  GPA          = Course Code of 2nd semester     (col 6)
                  Degree Gran. = Course Name + Credit + Marks + G. Letter (cols 7-10) */}
              <tr className="bg-light fw-bold text-center">
                <td colSpan={2} className="py-1 px-1 text-nowrap">Grand Total: {studentData.grand_total}</td>
                <td colSpan={2} className="py-1 px-1 text-nowrap">Average: {studentData.average}</td>
                <td colSpan={1} className="py-1 px-1 text-nowrap">Grade: {studentData.grade}</td>
                <td colSpan={1} className="py-1 px-1 text-nowrap">GPA: {studentData.gpa}</td>
                <td colSpan={4} className="py-1 px-1 text-nowrap">Degree Granted: Bachelor of {studentData.department}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signature Block */}
        <div 
  className="d-flex justify-content-between align-items-end mt-5 pt-1 signature-block" 
  style={{ 
    pageBreakInside: 'avoid',
    paddingLeft: '50px',  /* Waxay kan bidix gudaha u soo riixaysaa 40px */
    paddingRight: '50px'  /* Waxay kan midig gudaha u soo riixaysaa 40px */
  }}
>
  {/* --- SECTION 1: PRESIDENT --- */}
  <div className="text-center" style={{ width: 'auto', whiteSpace: 'nowrap' }}>
    <div style={{ minHeight: '50px' }} className="d-flex align-items-end justify-content-center">
      {studentData.presidentSignature && (
        <img 
          src={studentData.presidentSignature} 
          alt="President Signature" 
          style={{ maxHeight: '50px', width: 'auto', marginBottom: '2px' }} 
        />
      )}
    </div>

    <hr className="border-dark mb-1" style={{ width: '120px', margin: '0 auto 4px' }} />
    <div
      className="mb-0"
      style={{
        fontSize: '15px',
        color: '#000',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        fontFamily: '"PT Sans Narrow", Arial, sans-serif',
        fontStyle: 'normal'
      }}
    >
      {studentData.president}
    </div>
    <div style={{ fontSize: '13px', color: '#000' }}>The President</div>
  </div>

  {/* --- SECTION 2: ACADEMIC REGISTRAR --- */}
  <div className="text-center" style={{ width: 'auto', whiteSpace: 'nowrap' }}>
    <div style={{ minHeight: '50px' }} className="d-flex align-items-end justify-content-center">
      {studentData.registrarSignature && (
        <img 
          src={studentData.registrarSignature} 
          alt="Registrar Signature" 
          style={{ maxHeight: '50px', width: 'auto', marginBottom: '2px' }} 
        />
      )}
    </div>

    <hr className="border-dark mb-1" style={{ width: '120px', margin: '0 auto 4px' }} />
    <div
      className="mb-0"
      style={{
        fontSize: '15px',
        color: '#000',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        fontFamily: '"PT Sans Narrow", Arial, sans-serif',
        fontStyle: 'normal'
      }}
    >
      {studentData.registrar}
    </div>
    <div style={{ fontSize: '13px', color: '#000' }}>Academic Registrar</div>
  </div>

</div>
      </div>
    </div>
  );
};

export default EnTranscript;